import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useAuth } from "../../../AuthContext/AuthContext";
import axios from "../../../../configApi/axiosConfig";
import { APIURL } from "../../../../configApi/apiConfig";
import uploadFile from "../../../../hooks/uploadFile";
import {
  DOCS_DIGITALES_BS,
  DOCS_QUE_REQUIEREN_COMPRA_BS,
  ENDPOINTS_DIGITALES_BS,
  DOCUMENTO_IDS_BS,
  isRespaldo,
  getTotalRequiredBs,
} from "../constants/documentos-bs";

// Genera el PDF desde el endpoint, lo sube a storage y lo persiste en DocumentosSolicitudWeb.
// Si un documento no tiene idTipoDocumentoWEB, solo devuelve el blob URL sin persistir.
// idCompraBS se pasa a endpointFn solo para los documentos que lo necesitan
// (los demás lo ignoran, ver ENDPOINTS_DIGITALES_BS / apiConfig.js).
async function generarYPersistirDoc(docName, endpointFn, clientInfo, userData, idCompraBS) {
  const genResp = await axios.get(endpointFn(clientInfo.id, idCompraBS), { responseType: "blob" });
  const blob = new Blob([genResp.data], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);

  const idTipo = DOCUMENTO_IDS_BS[docName];
  if (!idTipo) return { url: blobUrl };

  try {
    const file = new File([blob], `${docName}.pdf`, { type: "application/pdf" });
    const uploadResp = await uploadFile(
      file,
      clientInfo.almacen,
      clientInfo.cedula,
      clientInfo.NumeroSolicitud,
      docName
    );
    if (!uploadResp?.url) return { url: blobUrl };

    await axios.post(
      APIURL.post_documentos(),
      {
        idCre_SolicitudWeb: clientInfo.id,
        idTipoDocumentoWEB: idTipo,
        RutaDocumento: uploadResp.url,
        Observacion: "",
        Usuario: userData?.Nombre ?? "Sistema",
        IdUsuario: userData?.idUsuario ?? 0,
      },
      { headers: { "Content-Type": "application/json" } }
    );

    URL.revokeObjectURL(blobUrl);
    return { url: uploadResp.url };
  } catch {
    // Fallo silencioso: mostramos el blob aunque no se haya persistido
    return { url: blobUrl };
  }
}

function computeProgress(completedFields2, showOnlyCorrections, filesToCorrect) {
  if (showOnlyCorrections) {
    const obligatorios = filesToCorrect.filter((f) => {
      const num = DOCUMENTO_IDS_BS[f];
      return num && ((num >= 1 && num <= 15) || num > 25);
    });
    if (!obligatorios.length) return 0;
    const completados = completedFields2.filter((f) => obligatorios.includes(f)).length;
    return (completados / obligatorios.length) * 100;
  }
  return (completedFields2.length / getTotalRequiredBs()) * 100;
}

export function useArchivosBs(clientInfo) {
  const { userData } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [files, setFiles] = useState({});
  const [filePreviews, setFilePreviews] = useState({});
  const [completedFields2, setCompletedFields2] = useState([]);
  const [filesToCorrect, setFilesToCorrect] = useState([]);
  const [showOnlyCorrections, setShowOnlyCorrections] = useState(false);
  const [refreshFiles, setRefreshFiles] = useState(false);
  const [initialTabSet, setInitialTabSet] = useState(false);
  const [activeTabInicial, setActiveTabInicial] = useState(null);
  const [isGenerandoDigitales, setIsGenerandoDigitales] = useState(false);
  const [idCompraBS, setIdCompraBS] = useState(null);

  useEffect(() => {
    if (!clientInfo.NumeroSolicitud) return;

    const fetchFiles = async () => {
      try {
        const resp = await axios.get(
          APIURL.get_documentos(clientInfo.id, clientInfo.idEstadoVerificacionDocumental)
        );
        if (resp.status !== 200 || !Array.isArray(resp.data)) return;

        // get_documentos (findBySolicitud) nunca trae documentos ya aprobados
        // (idEstadoDocumento = 3): ni la rama de idEstadoVerificacionDocumental=1
        // ni la de =3 lo incluyen. Sin esto, al volver a entrar en estado de
        // corrección, los documentos de subida manual ya aprobados por el
        // analista (Copia de Cédula, fotos, etc.) desaparecen de la pantalla.
        let documentosAprobados = [];
        try {
          const aprobadosResp = await axios.get(APIURL.get_documentosEstado(clientInfo.id, 3));
          if (Array.isArray(aprobadosResp.data)) documentosAprobados = aprobadosResp.data;
        } catch (err) {
          console.error("Error al consultar documentos aprobados:", err);
        }
        const idsYaTraidos = new Set(resp.data.map((f) => f.idDocumentosSolicitudWeb));
        const documentos = [
          ...resp.data,
          ...documentosAprobados.filter((f) => !idsYaTraidos.has(f.idDocumentosSolicitudWeb)),
        ];

        // Cre_SolicitudWeb.idCompra no se actualiza para Banco Solidario: hay que
        // buscar la compra real en la tabla Compra (idCre_SolicitudWeb +
        // idTipoFactura=43). Sin esto, los 4 documentos que dependen de compra
        // no se generan (ver DOCS_QUE_REQUIEREN_COMPRA_BS).
        let idCompraEncontrada = null;
        try {
          const compraResp = await axios.get(APIURL.getCompraBancoSolidarioPorSolicitud(clientInfo.id));
          idCompraEncontrada = compraResp.data?.success ? compraResp.data.idCompra : null;
        } catch (err) {
          console.error("Error al consultar la compra de Banco Solidario:", err);
        }
        setIdCompraBS(idCompraEncontrada);

        const uploadedFiles = {};
        const previews = {};
        const completed = new Set();
        const correctionsSet = new Set();

        documentos.forEach((file) => {
          const sectionName = Object.keys(DOCUMENTO_IDS_BS).find(
            (key) => DOCUMENTO_IDS_BS[key] === file.idTipoDocumentoWEB
          );
          if (!sectionName) return;

          const esDigital = DOCS_DIGITALES_BS.includes(sectionName);
          const fueRechazadoOAnulado = file.idEstadoDocumento === 4 || file.idEstadoDocumento === 5;

          // Un documento digital rechazado/anulado (ej. por vencimiento de
          // prefactura) no se puede corregir a mano: la subida está bloqueada
          // para estos. Se trata como "no generado" para que caiga en
          // pendientesDeGenerar y se regenere solo, en vez de quedar
          // atascado en "a corregir" sin ninguna acción posible.
          if (esDigital && fueRechazadoOAnulado) return;

          if (!uploadedFiles[sectionName]) {
            uploadedFiles[sectionName] = [];
            previews[sectionName] = [];
          }

          const fileName = file.RutaDocumento.split("/").pop();
          uploadedFiles[sectionName].push({
            idDocumentoSolicitudWeb: file.idDocumentosSolicitudWeb,
            name: fileName,
            url: file.RutaDocumento,
            type: file.RutaDocumento.endsWith(".pdf") ? "application/pdf" : "image/jpeg",
            estado: file.idEstadoDocumento,
            esDigital,
          });
          previews[sectionName].push(file.RutaDocumento);

          const idNum = file.idTipoDocumentoWEB;
          const esObligatorio = (idNum >= 1 && idNum <= 15) || idNum > 25;
          if (fueRechazadoOAnulado && esObligatorio) {
            correctionsSet.add(sectionName);
          } else if (!isRespaldo(sectionName)) {
            completed.add(sectionName);
          }
        });

        // Generar y persistir documentos digitales que aún no están en BD.
        // Los que dependen de compra (DOCS_QUE_REQUIEREN_COMPRA_BS) solo se
        // generan si ya se encontró la compra; si no, se dejan pendientes (la
        // pantalla muestra "falta la prefactura" para esos).
        // Primera entrada: lento (genera PDFs + sube + guarda). Entradas siguientes: carga instantánea desde BD.
        const pendientesDeGenerar = DOCS_DIGITALES_BS.filter((d) => {
          if (uploadedFiles[d]) return false;
          if (DOCS_QUE_REQUIEREN_COMPRA_BS.includes(d)) return Boolean(idCompraEncontrada);
          return true;
        });
        if (pendientesDeGenerar.length > 0) {
          setIsGenerandoDigitales(true);
          for (const docName of pendientesDeGenerar) {
            const endpointFn = ENDPOINTS_DIGITALES_BS[docName];
            if (!endpointFn) continue;
            try {
              const { url } = await generarYPersistirDoc(docName, endpointFn, clientInfo, userData, idCompraEncontrada);
              uploadedFiles[docName] = [
                { idDocumentoSolicitudWeb: null, name: `${docName}.pdf`, url, type: "application/pdf", estado: 1, esDigital: true },
              ];
              previews[docName] = [url];
              completed.add(docName);
            } catch (err) {
              console.error(`Error al generar documento digital "${docName}":`, err);
            }
          }
          setIsGenerandoDigitales(false);
        }

        if (correctionsSet.size > 0) {
          for (let i = 1; i <= 10; i++) correctionsSet.add(`Respaldo ${i}`);
        }

        setFiles(uploadedFiles);
        setFilePreviews(previews);
        setCompletedFields2([...completed]);
        setFilesToCorrect([...correctionsSet]);
        setShowOnlyCorrections(correctionsSet.size > 0);

        if (!initialTabSet) {
          const [first] = Array.from(correctionsSet);
          if (first) {
            setActiveTabInicial(first);
            setInitialTabSet(true);
          }
        }
      } catch (err) {
        enqueueSnackbar("Error al cargar los documentos.", { variant: "error" });
        console.error(err);
      }
    };

    fetchFiles();
  }, [clientInfo.id, refreshFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actualizar filePreviews cuando el usuario agrega archivos locales nuevos (File/Blob)
  useEffect(() => {
    const localBlobUrls = [];
    const updatedPreviews = {};

    Object.keys(files).forEach((field) => {
      updatedPreviews[field] = files[field].map((file) => {
        if (file instanceof File || file instanceof Blob) {
          const url = URL.createObjectURL(file);
          localBlobUrls.push(url);
          return url;
        }
        return file.url || file;
      });
    });

    if (Object.keys(updatedPreviews).length > 0) {
      setFilePreviews(updatedPreviews);
    }

    return () => {
      localBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  const progress = computeProgress(
    completedFields2,
    showOnlyCorrections,
    filesToCorrect
  );
  const progressColor = progress < 50 ? "#FF0000" : progress < 80 ? "#FF9800" : "#4CAF50";

  return {
    files, setFiles,
    filePreviews, setFilePreviews,
    completedFields2,
    filesToCorrect,
    showOnlyCorrections,
    setRefreshFiles,
    activeTabInicial,
    isGenerandoDigitales,
    idCompraBS,
    progress,
    progressColor,
  };
}
